import { useMemo, useState } from 'react';

import { css, styled, type Theme } from '@mui/material';
import { IcCloseSmallRegular } from '@kep/kcds-icons';

import { IconButtonGhost } from '../IconButtonGhost';
import { Typography } from '../Typography';
import SectionMessageCheckbox from './SectionMessageCheckbox';
import { SectionMessageContext } from './SectionMessageContext';
import _SectionMessageIcon from './SectionMessageIcon';
import SectionMessageLink from './SectionMessageLink';
import SectionMessageTextList from './SectionMessageTextList';
import { sectionMessageClasses } from './theme';

import type { MouseEvent } from 'react';
import type { SectionMessageVariant } from './constants';
import type { SectionMessageOwnerState, SectionMessageProps } from './types';

const SectionMessage = (props: SectionMessageProps) => {
  const {
    children,
    icon = true, // theme defaultProps에서 이동
    onClose,
    title,
    variant = 'error', // theme defaultProps에서 이동
    ...others
  } = props;

  const [checkboxChecked, setCheckboxChecked] = useState<boolean>(false);

  const ownerState: SectionMessageOwnerState = {
    ...props,
    icon,
    variant,
  };

  const handleClose = (event: MouseEvent<HTMLButtonElement>) => {
    if (onClose) {
      onClose(event, checkboxChecked);
    }
  };

  const contextValue = useMemo(
    () => ({
      checkboxChecked,
      setCheckboxChecked,
    }),
    [checkboxChecked],
  );

  return (
    <SectionMessageContext.Provider value={contextValue}>
      <SectionMessageRoot ownerState={ownerState} {...others}>
        {/* TODO (@walter-24) icon을 Status를 사용할지, 직접 구현할지 확인 필요 (https://www.figma.com/design/ZeLzIUbJDNc6z8U8fPLFKW?node-id=1651-85604#1133003989) */}
        {icon && <SectionMessageIcon variant={variant} ownerState={ownerState} />}
        <SectionMessageTextsContainer ownerState={ownerState}>
          {title && (
            <SectionMessageTitle
              variant="label/semibold/xs"
              color="text/default"
              ownerState={ownerState}
            >
              {title}
            </SectionMessageTitle>
          )}
          <SectionMessageBody
            variant="body/normal/xs"
            color="text/weak/default"
            ownerState={ownerState}
          >
            {children}
          </SectionMessageBody>
        </SectionMessageTextsContainer>
        {onClose && (
          <SectionMessageCloseButton size="sm" onClick={handleClose} ownerState={ownerState}>
            <IcCloseSmallRegular />
          </SectionMessageCloseButton>
        )}
      </SectionMessageRoot>
    </SectionMessageContext.Provider>
  );
};

export default SectionMessage;

// Styled Components
const SectionMessageRoot = styled('div', {
  target: sectionMessageClasses.root,
})<{ ownerState: SectionMessageOwnerState }>(({ ownerState, theme }) => {
  const { variant } = ownerState;

  return css`
    padding: calc(16px - 1px);
    display: flex;
    align-items: flex-start;
    gap: 10px;
    border-radius: 1px;

    ${getVariantRootStyles(variant, theme)}
  `;
});

const SectionMessageIcon = styled(_SectionMessageIcon, {
  target: sectionMessageClasses.icon,
})<{ ownerState: SectionMessageOwnerState }>(({ ownerState, theme }) => {
  const { variant } = ownerState;

  return css`
    font-size: 20px;

    ${getVariantIconStyles(variant, theme)}
  `;
});

const SectionMessageTextsContainer = styled('div', {
  target: sectionMessageClasses.textsContainer,
})<{ ownerState: SectionMessageOwnerState }>(
  () => css`
    min-height: 20px;
    flex: 1 0 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 4px;
  `,
);

const SectionMessageTitle = styled(Typography, {
  target: sectionMessageClasses.title,
})<{ ownerState: SectionMessageOwnerState }>(css``);

const SectionMessageBody = styled(Typography, {
  target: sectionMessageClasses.body,
})<{ ownerState: SectionMessageOwnerState }>(css``);

const SectionMessageCloseButton = styled(IconButtonGhost, {
  target: sectionMessageClasses.closeButton,
})<{ ownerState: SectionMessageOwnerState }>(css``);

// Style helper functions
const getVariantRootStyles = (variant: SectionMessageVariant, theme: Theme) => {
  const variantStylesMap = {
    error: css`
      background-color: ${theme.color['surface/danger/weakest/default']};
      border: 1px solid ${theme.color['border/danger/weakest/default']};
    `,
    warning: css`
      background-color: ${theme.color['surface/warning/weakest/default']};
      border: 1px solid ${theme.color['border/warning/weakest/default']};
    `,
    information: css`
      background-color: ${theme.color['surface/informative/weakest/default']};
      border: 1px solid ${theme.color['border/informative/weakest/default']};
    `,
  };
  return variantStylesMap[variant];
};

const getVariantIconStyles = (variant: SectionMessageVariant, theme: Theme) => {
  const variantStylesMap = {
    error: css`
      color: ${theme.color['surface/danger/default']};
    `,
    warning: css`
      color: ${theme.color['icon/warning/default']};
    `,
    information: css`
      color: ${theme.color['icon/informative/default']};
    `,
  };
  return variantStylesMap[variant];
};

SectionMessage.Link = SectionMessageLink;
SectionMessage.Checkbox = SectionMessageCheckbox;
SectionMessage.TextList = SectionMessageTextList;
